import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import Input from "./Input";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const Header = ({ isScrolled }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { cartList } = useSelector((state) => state.general);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [cartCount, setCartCount] = useState(0);

  const userMenuRef = useRef(null);

  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  /* ================= CART COUNT ================= */
  useEffect(() => {
    if (isAuthenticated) {
      setCartCount(cartList?.length || 0);
    } else {
      const localCount = parseInt(localStorage.getItem("cartCount") || "0", 10);
      setCartCount(localCount);
    }
  }, [cartList, isAuthenticated]);

  /* ================= CLOSE USER MENU OUTSIDE ================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ================= SEARCH ================= */
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/product-listing?search=${encodeURIComponent(searchQuery)}`);
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  /* ================= RTL ================= */
  const isRTL = i18n.language === "ar";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-md" : "bg-[#F5F3ED]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">

            {/* ================= LOGO ================= */}
            <Link to="/" className="flex items-center">
              <img
                src="/assets/images/logo.png"
                alt="logo"
                className="w-12 h-12 rounded-xl object-cover"
              />
            </Link>

            {/* ================= DESKTOP NAV ================= */}
            <nav className="hidden lg:flex items-center gap-6 font-bold text-[15px]">
              <button
                onClick={() => navigate("/")}
                className={`hover:text-primary transition ${isActive("/") ? "text-primary" : ""}`}
              >
                {t("home")}
              </button>

              <button
                onClick={() => navigate("/product-listing")}
                className={`hover:text-primary transition ${isActive("/product-listing") ? "text-primary" : ""}`}
              >
                {t("categories")}
              </button>

              <button
                onClick={() => navigate("/about-us")}
                className={`hover:text-primary transition ${isActive("/about-us") ? "text-primary" : ""}`}
              >
                {t("navigation.aboutUs")}
              </button>

              {/* Wishlist */}
              <button
                onClick={() => navigate("/wishlist")}
                className={`hover:text-primary transition ${isActive("/wishlist") ? "text-primary" : ""}`}
              >
                {t("wishlist")}
              </button>

              {/* Orders */}
              {isAuthenticated && (
                <button
                  onClick={() => navigate("/order-history")}
                  className={`hover:text-primary transition ${isActive("/order-history") ? "text-primary" : ""}`}
                >
                  {t("orders")}
                </button>
              )}
            </nav>

            {/* ================= RIGHT SIDE ================= */}
            <div className="flex items-center gap-3">

              {/* SEARCH - DESKTOP */}
              <form onSubmit={handleSearch} className="hidden md:flex relative">
                <Input
                  type="search"
                  placeholder={t("search.placeholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-40 md:w-52 lg:w-64 pl-10 rounded-full"
                />
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                />
              </form>

              {/* CART */}
              <Link to="/shopping-cart" className="relative p-2">
                <Icon name="ShoppingCart" size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </Link>

              {/* USER MENU - DESKTOP */}
              <div className="relative hidden lg:block" ref={userMenuRef}>
                <button onClick={() => setIsUserOpen(!isUserOpen)} className="p-2">
                  <Icon name="User" size={22} />
                </button>
                {isUserOpen && (
                  <div className="absolute -right-20 mt-2 w-40 bg-white border rounded-xl shadow-lg p-2">
                    {isAuthenticated ? (
                      <>
                        <Link to="/edit-profile" className="block px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("userMenu.myAccount")}
                        </Link>
                        <Link to="/wishlist" className="block px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("wishlist")}
                        </Link>
                        <Link to="/order-history" className="block px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("orders")}
                        </Link>
                        <button onClick={handleLogout} className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("userMenu.signOut")}
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" className="block px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("userMenu.signIn")}
                        </Link>
                        <Link to="/register" className="block px-3 py-2 hover:bg-gray-100 rounded-md">
                          {t("userMenu.createAccount")}
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* MOBILE MENU BUTTON */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2">
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* ================= MOBILE MENU ================= */}
        {isMobileMenuOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
            <div className={`fixed top-0 right-0 h-full w-3/4 max-w-sm bg-white z-50 shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
              <div className="p-6 space-y-6">
                {/* Close */}
                <div className="flex justify-end">
                  <button onClick={() => setIsMobileMenuOpen(false)}>
                    <Icon name="X" size={26} />
                  </button>
                </div>

                {/* Links */}
                {[
                  { label: t("home"), path: "/" },
                  { label: t("categories"), path: "/product-listing" },
                  { label: t("navigation.aboutUs"), path: "/about-us" },
                  { label: t("wishlist"), path: "/wishlist" },
                  ...(isAuthenticated ? [{ label: t("orders"), path: "/order-history" }] : []),
                ].map((link) => (
                  <button
                    key={link.path}
                    onClick={() => {
                      navigate(link.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-right text-lg font-bold"
                  >
                    {link.label}
                  </button>
                ))}

                {/* Search */}
                <form onSubmit={handleSearch} className="relative mt-6">
                  <Input
                    type="search"
                    placeholder={t("search.placeholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10"
                  />
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                  />
                </form>

                {/* Auth */}
                <div className="border-t pt-6 mt-6">
                  {isAuthenticated ? (
                    <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="block w-full text-right text-lg font-bold">
                      {t("userMenu.signOut")}
                    </button>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block text-right text-lg font-bold">
                        {t("userMenu.signIn")}
                      </Link>
                      <Link to="/register" onClick={() => setIsMobileMenuOpen(false)} className="block text-right text-lg font-bold mt-4">
                        {t("userMenu.createAccount")}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* spacer */}
      <div className="h-16" />
    </>
  );
};

export default Header;
